import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import Footer1 from "../components/Footer1";
import MobileNav from "../components/MobileNav";
import "./AllPaintings.css";
import paintingsData from "../data/paintings.json";

// --- MINIMALIST GALLERY ITEM & INTERCEPTION MODAL ---
// --- MINIMALIST GALLERY ITEM & INTERCEPTION MODAL ---
const GalleryItem = ({ item, index, viewMode }) => {

    const [isHovered, setIsHovered] = useState(false);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false); // State for Read More
    const [baseImage, setBaseImage] = useState(item.images?.[0]?.imageUrl || "https://via.placeholder.com/600x800?text=Masterpiece");
    const [hoverImage, setHoverImage] = useState(item.images?.[1]?.imageUrl || null);
    const navigate = useNavigate();

    useEffect(() => {
        if (viewMode === 'grid') {
            setBaseImage(item.images?.[0]?.imageUrl || "https://via.placeholder.com/600x800?text=Masterpiece");
            setHoverImage(item.images?.[1]?.imageUrl || null);
        } else {
            setBaseImage(item.images?.[1]?.imageUrl || "https://via.placeholder.com/600x800?text=Masterpiece");
            setHoverImage(item.images?.[0]?.imageUrl || null);
        }
    }, [viewMode, item]);

    const isReverse = index % 2 !== 0;
    const hasSeries = item.series && item.series.trim() !== "";
    const tagLabel = hasSeries ? `Anthology Piece` : "Singular Masterwork";
    const tagClass = hasSeries ? "tag-series" : "tag-singular";

    // Text Truncation & HTML Handling Logic
    const rawStory = item.story || "A profound narrative captured through unparalleled texture and form, exclusively by Anjaan Vivek.";
    
    // Strip HTML tags so the "short preview" is clean plain text
    const stripHtml = (html) => html.replace(/<[^>]*>?/gm, '');
    const plainTextStory = stripHtml(rawStory);
    
    const maxLength = 150;
    const isLongText = plainTextStory.length > maxLength;

    // Handle the click interception
    const handleCardClick = () => {
        if (hasSeries) {
            setShowPrompt(true); // Open the choice modal
        } else {
            navigate(`/originalDetails/${item.id}`); // Direct navigation for single pieces
        }
    };

    // The Luxury Choice Modal
    const renderPromptModal = () => (
        <AnimatePresence>
            {showPrompt && (
                <div className="elite-modal-overlay prompt-overlay" onClick={() => setShowPrompt(false)}>
                    <motion.div
                        className="prompt-box"
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button className="prompt-close" onClick={() => setShowPrompt(false)}>&times;</button>
                        <span className="prompt-eyebrow">Anthology Piece</span>
                        <h3 className="prompt-title">'{item.title}'</h3>
                        <p className="prompt-desc">This masterwork belongs to the highly curated <strong>{item.series} Anthology</strong>. How would you like to proceed?</p>

                        <div className="prompt-actions">
                            <button
                                className="btn-prompt-primary"
                                onClick={() => {
                                    if (item.series === "The World Frames") {
                                        navigate("/worldFrameSeries");
                                    } else {
                                        navigate(`/series/${item.series}`);
                                    }
                                }}
                            >
                                Explore Complete Anthology
                            </button>
                            <button className="btn-prompt-secondary" onClick={() => navigate(`/originalDetails/${item.id}`)}>
                                Inspect Only This Piece
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );

    const gridFade = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
    };

    const perspectiveReveal = {
        hidden: { opacity: 0, y: 60, rotateX: 5, scale: 0.97 },
        visible: { opacity: 1, y: 0, rotateX: 0, scale: 1, transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] } }
    };

    // --- GRID VIEW RENDER ---
    if (viewMode === 'grid') {
        return (
            <>
                <motion.div className="elite-grid-card" variants={gridFade} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
                    <div className="grid-image-wrapper" onClick={handleCardClick}>
                        <img src={baseImage} alt={item.title} className="grid-base-img default-img" />

                        {hoverImage && (
                            <img src={hoverImage} alt={`${item.title} Alternate`} className="grid-base-img hover-img" />
                        )}
                    </div>

                    <div className="grid-text-content" onClick={handleCardClick}>
                        <span className={`curatorial-tag ${tagClass}`}>{tagLabel}</span>
                        <h3>{item.title}</h3>
                        <span className="painting-series">{hasSeries ? `The ${item.series} Anthology` : "Singular Masterwork"}</span>
                    </div>
                </motion.div>
                {renderPromptModal()}
            </>
        );
    }

    // --- PERSPECTIVE VIEW RENDER ---
    return (
        <>
            <motion.div
                className={`elite-gallery-item ${isReverse ? 'reverse' : ''}`}
                initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} variants={perspectiveReveal}
            >
                <div className="elite-item-image hover-crossfade" onClick={handleCardClick} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
                    <img src={baseImage} alt={item.title} className={`base-img ${isHovered && hoverImage ? 'hidden' : ''}`} />
                    {hoverImage && <img src={hoverImage} alt={`${item.title} in environment`} className={`hover-img ${isHovered ? 'visible' : ''}`} />}
                </div>

                <div className="elite-item-details">
                    <span className={`curatorial-tag ${tagClass}`}>{tagLabel}</span>
                    <h3 className="elite-item-title">{item.title}</h3>
                    
                    {/* NEW: Safe HTML / Read More Handling */}
                    <div className="elite-story-container" style={{ marginBottom: "30px" }}>
                        {isExpanded ? (
                            <div 
                                className="elite-item-story html-formatted" 
                                dangerouslySetInnerHTML={{ __html: rawStory }} 
                            />
                        ) : (
                            <p className="elite-item-story" style={{ whiteSpace: "pre-line", marginBottom: 0 }}>
                                {isLongText ? `${plainTextStory.substring(0, maxLength)}...` : plainTextStory}
                            </p>
                        )}

                        {isLongText && (
                            <span 
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevents image click navigation
                                    setIsExpanded(!isExpanded);
                                }}
                                style={{
                                    color: "var(--accent-champagne)",
                                    cursor: "pointer",
                                    fontWeight: "500",
                                    fontSize: "0.9rem",
                                    display: "inline-block",
                                    marginTop: "8px"
                                }}
                            >
                                {isExpanded ? "Read Less" : "Read More"}
                            </span>
                        )}
                    </div>

                    <div className="elite-action-group">
                        <button className="btn-view-details" onClick={handleCardClick}>
                            Inspect Masterwork
                        </button>
                    </div>
                </div>
            </motion.div>
            {renderPromptModal()}
        </>
    );
};

// --- MAIN PAGE COMPONENT ---
const AllPaintings = () => {
    const [paintings, setPaintings] = useState([]);
    const [filteredPaintings, setFilteredPaintings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("All Paintings");
    const [viewMode, setViewMode] = useState("grid"); // Defaults to grid

    const TABS = ["All Paintings", "Mythological", "Positive Energy", "Pictorial Biography"];
    const { category } = useParams();
    const { scrollYProgress } = useScroll();
    const bannerY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

    // ✅ TOGGLE THIS BOOLEAN: true = Static Image, false = Looping Video
    const useImageBanner = false;

    useEffect(() => {
        window.scrollTo(0, 0);

        // 1. Lock screen scroll & set loading to true
        setLoading(true);
        document.body.style.overflow = "hidden";

        // 2. Fetch the data immediately so the DOM can build in the background
        setPaintings(paintingsData);
        setFilteredPaintings(paintingsData);
        setActiveTab(category ? category : "All Paintings");

        // 3. Keep the aesthetic loader on screen for 1.5 seconds while images fetch in background
        const timer = setTimeout(() => {
            setLoading(false);
            document.body.style.overflow = "auto"; // Unlock scroll
        }, 1500);

        return () => {
            clearTimeout(timer);
            document.body.style.overflow = "auto";
        };
    }, [category]);

    useEffect(() => {
        if (activeTab === "All Paintings") {
            setFilteredPaintings(paintings);
        } else {
            const filtered = paintings.filter(p => {
                const cat = (p.category || "").toLowerCase();
                const series = (p.series || "").toLowerCase();
                const target = activeTab.toLowerCase();
                return cat.includes(target) || series.includes(target);
            });
            setFilteredPaintings(filtered);
        }
    }, [activeTab, paintings]);

    // Added a delay so the content waits for the loader to start fading out before it moves up
    const fadeUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.3 } }
    };

    return (
        <div className="all-paintings-elite-page">

            {/* --- AESTHETIC BACKGROUND LOADER --- */}
            <AnimatePresence>
                {loading && (
                    <motion.div
                        key="elite-loader"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 99999,
                            backgroundColor: "#ffffff",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >
                        <motion.span
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                            style={{
                                fontFamily: "'Forum', serif", // Matches your CSS typography
                                fontSize: "1.2rem",
                                textTransform: "uppercase",
                                letterSpacing: "6px",
                                color: "#111111",
                                marginBottom: "20px"
                            }}
                        >
                            Curating Archive
                        </motion.span>
                        <div style={{ width: "80px", height: "1px", backgroundColor: "rgba(0,0,0,0.05)", overflow: "hidden", position: "relative" }}>
                            <motion.div
                                initial={{ x: "-100%" }}
                                animate={{ x: "100%" }}
                                transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    backgroundColor: "#c4a36e",
                                    position: "absolute"
                                }}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <MobileNav />

            {/* --- PARALLAX BANNER (IMAGE OR VIDEO) --- */}
            <div className="elite-archive-banner-wrapper">
                {useImageBanner ? (
                    <motion.div className="elite-archive-banner-bg" style={{ y: bannerY }}></motion.div>
                ) : (
                    <motion.div className="archive-video-wrapper" style={{ y: bannerY }}>
                        <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="archive-background-video"
                            /* 1. Instantly loads a static image frame while the video buffers */
                            poster="https://res.cloudinary.com/digze3wuc/video/upload/v1785060364/bgAllPaintings_2_xyc403.mp4"
                        >
                            {/* 2. Uses Cloudinary q_auto,f_auto to serve a lightweight MP4 instead of a heavy MOV */}
                            <source src="https://res.cloudinary.com/digze3wuc/video/upload/v1785060364/bgAllPaintings_2_xyc403.mp4" type="video/mp4" />
                        </video>
                    </motion.div>
                )}

                <div className="banner-overlay-soft"></div>

                {/* Tied to `!loading` so it animates IN after the loader finishes */}
                <motion.div className="banner-text-content" initial="hidden" animate={!loading ? "visible" : "hidden"} variants={fadeUp}>
                    <span className="banner-eyebrow">The Complete Archive</span>
                    <h1 className="banner-main-title">Curated Masterpieces</h1>
                    <div className="banner-delicate-line"></div>
                </motion.div>
            </div>

            {/* DOM renders instantly to fetch images in the background */}
            <main className="elite-gallery-main">
                <div className="elite-controls-bar">
                    <div className="elite-filter-tabs">
                        {TABS.map((tab) => (
                            <button key={tab} className={`elite-tab-btn ${activeTab === tab ? "active" : ""}`} onClick={() => setActiveTab(tab)}>
                                {tab}
                            </button>
                        ))}
                    </div>

                    <div className="elite-view-toggle">
                        <button className={`view-btn ${viewMode === 'perspective' ? 'active' : ''}`} onClick={() => setViewMode('perspective')} title="Perspective Layout">
                            <i className="fa-solid fa-layer-group"></i> <span>Perspective</span>
                        </button>
                        <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')} title="Grid Layout">
                            <i className="fa-solid fa-border-all"></i> <span>Gallery</span>
                        </button>
                    </div>
                </div>

                <div className="elite-gallery-list-wrapper">
                    {filteredPaintings.length === 0 ? (
                        <div className="elite-empty-state">
                            <p>No masterworks found in this curation.</p>
                            <button onClick={() => setActiveTab("All Paintings")} className="btn-elite-secondary mt-4">
                                View Entire Archive
                            </button>
                        </div>
                    ) : (
                        // UNIFIED ARCHIVE DISPLAY
                        <div className={viewMode === 'grid' ? "elite-unified-grid" : "elite-unified-perspective"}>
                            {filteredPaintings.map((painting, index) => (
                                <GalleryItem key={painting.id} item={painting} index={index} viewMode={viewMode} />
                            ))}
                        </div>
                    )}
                </div>
            </main>
            <Footer1 />
        </div>
    );
};

export default AllPaintings;